import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';
import { AnswerBoxView } from '@/features/laneChooser/game/objects/AnswerBoxView';
import { computeInitialLiftYFromWidth, getResponsiveWidthPx } from '@/features/laneChooser/game/utils/responsive';

import type {
	LaneChooserAnswerResolution,
	LaneChooserAnswerAsset,
	LaneChooserAnswers,
	LaneChooserLottieAsset,
	LaneChooserPlayer,
	LaneChooserQuestionPayload,
} from '@/features/laneChooser/game/types';
import type { AnswerBoxPosition } from '@/features/laneChooser/game/utils/answerLabelLayout';
import type { LaneShapeMetrics } from '@/features/laneChooser/game/utils/laneShape';

type AnswersControllerConfig = {
	// Scene reference used for animations, text creation, and per-frame updates.
	scene: Phaser.Scene;
	// Parent world container that keeps the answer stream inside the lane scene.
	container: Phaser.GameObjects.Container;
	// Top-most overlay container used for the resolved answer row while it exits past the player.
	exitContainer: Phaser.GameObjects.Container;
	// Lane display object that should render above the answer row during the initial lift phase.
	laneDisplayObject: Phaser.GameObjects.GameObject;
	// Shared lane geometry used to align the stream to the current lane layout.
	laneMetrics: LaneShapeMetrics;
	// Current lane count used to place one answer box per visible lane.
	laneLinesCount: number;
	// Responsive lane-top width percentage used by the established slot-position math.
	topWidthPct: number;
	// CSS viewport width from React used by established responsive sizing rules.
	viewportWidth: number;
	// X-axis multiplier from CSS pixels into Phaser render pixels.
	renderScaleX: number;
	// Y-axis multiplier from CSS pixels into Phaser render pixels.
	renderScaleY: number;
	// Current question payload that provides the visible answer labels.
	question: LaneChooserQuestionPayload | null;
	// Theme-owned answer box textures and label colors.
	themeConfig: LaneChooserAnswers;
	// Player theme used to keep the answer stream clear of the character at the bottom.
	playerThemeConfig: LaneChooserPlayer;
	lottieSourceRoot: HTMLElement;
	onRoundExitComplete: () => void;
};

const ANSWER_ROW_INITIAL_SCALE = 0.8;
const ANSWER_ROW_MIDDLE_SCALE = 1;
const ANSWER_ROW_FINAL_SCALE = 1.6;
const STATIC_BOX_INITIAL_OVERLAP_RATIO = 0.2;
const INITIAL_LIFT_DURATION_SECONDS = 2;
const TRAVEL_DURATION_SECONDS = 34;
const TOTAL_TRAVEL_DURATION_SECONDS = INITIAL_LIFT_DURATION_SECONDS + TRAVEL_DURATION_SECONDS;
const ANSWER_PANEL_HEIGHT_PX = 92;
const TWO_LANES_WIDTH_OFFSET_PCT = 15;
const DEFAULT_WIDTH_OFFSET_PCT = 5;
const SHIFT_TOWARD_LEFT_LINE = -0.1;
const SHIFT_TOWARD_RIGHT_LINE = 0.1;
const SECOND_BOX_LEFT_NUDGE = -0.01;
const THIRD_BOX_RIGHT_NUDGE = 0.01;
const DEFAULT_TIMELINE_SCALE = 1;
const EXIT_SCALE = 3;
const EXIT_DURATION_MS = 3500;
const EXIT_ROW_DEPTH = 30;

function isLottieAnswerAsset(asset: LaneChooserAnswerAsset): asset is LaneChooserLottieAsset {
	return 'animationData' in asset;
}

// Chooses the same left/middle/right visual slots that the established React answer row used.
function getAnswerBoxPosition(index: number, count: number): AnswerBoxPosition {
	if (index === 0) {
		return 'left';
	}

	if (index === count - 1) {
		return 'right';
	}

	return 'middle';
}

// Reproduces the subtle within-lane nudges from the established React answer row.
function getLaneFraction(index: number, lanesCount: number): number {
	const laneCenter = (index + 0.5) / lanesCount;
	let withinLaneShift = 0;

	if (index === 0 && lanesCount > 1) {
		withinLaneShift = SHIFT_TOWARD_LEFT_LINE / lanesCount;
	} else if (index === lanesCount - 1 && lanesCount > 1) {
		withinLaneShift = SHIFT_TOWARD_RIGHT_LINE / lanesCount;
	} else if (index === 1) {
		withinLaneShift = SECOND_BOX_LEFT_NUDGE;
	} else if (index === 2) {
		withinLaneShift = THIRD_BOX_RIGHT_NUDGE;
	}

	return Phaser.Math.Clamp(laneCenter + withinLaneShift, 0.01, 0.98);
}

export class AnswersController {
	// Scene reference used to create sprites, text, and animations.
	private readonly scene: Phaser.Scene;
	// Parent world container that owns the full moving answer row.
	private readonly container: Phaser.GameObjects.Container;
	// Overlay container used to temporarily lift the resolved row above the player layer.
	private readonly exitContainer: Phaser.GameObjects.Container;
	// Lane display object used as the reordering anchor for the initial and travel phases.
	private readonly laneDisplayObject: Phaser.GameObjects.GameObject;
	// Shared lane shape metrics used to align the row with the current lane render.
	private readonly laneMetrics: LaneShapeMetrics;
	// Theme-owned answer-box textures, widths, and label color.
	private readonly themeConfig: LaneChooserAnswers;
	// Theme-owned player settings used to keep the answer stream clear of the character.
	private readonly playerThemeConfig: LaneChooserPlayer;
	private readonly lottieSourceRoot: HTMLElement;
	private readonly onRoundExitComplete: () => void;
	// CSS viewport width from React used by the established responsive layout rules.
	private readonly viewportWidth: number;
	// X-axis multiplier from CSS pixels into Phaser render pixels.
	private readonly renderScaleX: number;
	// Y-axis multiplier from CSS pixels into Phaser render pixels.
	private readonly renderScaleY: number;
	// Current lane count used to place each answer over its lane slot.
	private readonly laneLinesCount: number;
	// Current question labels to render inside the animated answer boxes.
	private readonly labels: string[];
	// Root container whose translate/scale simulate the established GSAP timeline.
	private readonly rowContainer: Phaser.GameObjects.Container;
	// All answer-box runtime instances currently visible in the scene.
	private readonly runtimes: AnswerBoxView[];
	// Active tween that sends the answer row off-screen after a jump-based answer is resolved.
	private exitTween: Phaser.Tweens.Tween | null = null;
	// Current timeline multiplier used to speed up or reverse the whole answer stream.
	private motionScale = DEFAULT_TIMELINE_SCALE;
	// Current direction multiplier used to reverse the established timeline.
	private motionDirection: 1 | -1 = 1;
	// Timeline cursor in seconds for the manual two-stage established animation.
	private elapsedSeconds = 0;
	// Tracks whether the answer row is currently ordered above the lane display object.
	private rowAboveLane = false;
	// Prevents duplicate answer requests while waiting for React to resolve the current jump.
	private awaitingAnswerResolution = false;
	// Prevents duplicate exit transitions after the current answer has been resolved.
	private exitStarted = false;
	// Tracks whether the resolved row has already been lifted above the player layer.
	private exitAbovePlayer = false;
	// Mirrors the established paused-at-bottom state for the box spritesheet animation itself.
	private waitingAtBottom = false;
	// Negative lift applied during the first short stage of the established timeline.
	private readonly initialLiftY: number;
	// Final bottom target from the established row math, measured from the lane top.
	private readonly bottomTargetY: number;
	// Prevents updates after the controller has been destroyed.
	private destroyed = false;

	constructor({
		scene,
		container,
		exitContainer,
		laneDisplayObject,
		laneMetrics,
		laneLinesCount,
		topWidthPct,
		viewportWidth,
		renderScaleX,
		renderScaleY,
		question,
		themeConfig,
		playerThemeConfig,
		lottieSourceRoot,
		onRoundExitComplete,
	}: AnswersControllerConfig) {
		this.scene = scene;
		this.container = container;
		this.exitContainer = exitContainer;
		this.laneDisplayObject = laneDisplayObject;
		this.laneMetrics = laneMetrics;
		this.themeConfig = themeConfig;
		this.playerThemeConfig = playerThemeConfig;
		this.lottieSourceRoot = lottieSourceRoot;
		this.onRoundExitComplete = onRoundExitComplete;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;
		this.laneLinesCount = Math.max(1, Math.min(4, laneLinesCount));
		this.labels = question?.answerLabels?.slice(0, 4) ?? [];
		this.initialLiftY = this.getInitialLiftY();
		this.bottomTargetY = this.getBottomTargetY();

		this.rowContainer = this.scene.add.container(this.laneMetrics.centerX, this.laneMetrics.topOffset);
		this.runtimes = this.createAnswerBoxes(topWidthPct);
		this.container.add(this.rowContainer);

		this.syncLayering(true);
		this.syncTimelineState();
	}

	// Advances the manual established timeline so boost and reverse work the same way as the React version.
	update(delta: number) {
		if (this.destroyed) {
			return;
		}

		this.runtimes.forEach((runtime) => runtime.update());

		if (delta <= 0 || this.exitStarted || this.motionScale <= 0) {
			return;
		}

		const signedScale = (this.motionDirection === -1 ? -1 : 1) * Math.abs(this.motionScale);
		const nextElapsedSeconds = this.elapsedSeconds + (delta / 1000) * signedScale;

		this.elapsedSeconds = Phaser.Math.Clamp(nextElapsedSeconds, 0, TOTAL_TRAVEL_DURATION_SECONDS);
		this.syncTimelineState();
	}

	// Applies the same signed time-scale model as the established GSAP answer-row timeline.
	setMotion(motionScale: number, motionDirection: 1 | -1) {
		this.motionScale = Math.max(0, motionScale);
		this.motionDirection = motionDirection === -1 ? -1 : 1;
	}

	// Pauses or resumes the themed box animations while the row is waiting at the player.
	setWaitingAtBottom(isWaitingAtBottom: boolean) {
		if (this.destroyed || this.waitingAtBottom === isWaitingAtBottom) {
			return;
		}

		this.waitingAtBottom = isWaitingAtBottom;
		this.syncBoxAnimationPlayback();
	}

	// Reports whether the answer row has reached the player-facing answer position and is waiting for input.
	isAtBottom(): boolean {
		return !this.destroyed && !this.exitStarted && this.elapsedSeconds >= TOTAL_TRAVEL_DURATION_SECONDS;
	}

	// Arms the current row for jump-based answer confirmation once it has reached the bottom target.
	beginAnswerRequest(): boolean {
		if (this.destroyed || this.awaitingAnswerResolution || this.exitStarted || !this.isAtBottom()) {
			return false;
		}

		this.awaitingAnswerResolution = true;
		return true;
	}

	// Applies the resolved answer outcome, decorates the row, and sends it off-screen like the established game.
	applyAnswerResolution(resolution: LaneChooserAnswerResolution) {
		if (this.destroyed || this.exitStarted) {
			return;
		}

		this.awaitingAnswerResolution = false;
		this.setWaitingAtBottom(false);
		this.decorateResolvedAnswer(resolution);
		this.startExitTransition();
	}

	// Lifts the exiting row above the player once the jump has reached its downward phase.
	promoteExitAbovePlayer() {
		if (this.destroyed || !this.exitStarted || this.exitAbovePlayer) {
			return;
		}

		if (this.rowContainer.parentContainer !== this.exitContainer) {
			this.rowContainer.parentContainer?.remove(this.rowContainer);
			this.exitContainer.add(this.rowContainer);
		}

		this.rowContainer.setDepth(EXIT_ROW_DEPTH);
		this.exitAbovePlayer = true;
	}

	// Cleans up all scene-owned display objects created by this controller.
	destroy() {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.exitTween?.stop();
		this.exitTween = null;
		this.runtimes.forEach((runtime) => runtime.destroy());
		this.rowContainer.destroy();
	}

	// Creates one themed answer box per visible slot and aligns each slot with the established row math.
	private createAnswerBoxes(topWidthPct: number): AnswerBoxView[] {
		const count = Math.min(this.labels.length || 4, 4);
		const adjustedTopWidthPct =
			topWidthPct - (this.laneLinesCount === 2 ? TWO_LANES_WIDTH_OFFSET_PCT : DEFAULT_WIDTH_OFFSET_PCT);
		const rowWidthPx = (this.laneMetrics.width * adjustedTopWidthPct) / 100;
		const rowLeftX = -rowWidthPx / 2;
		const boxWidth = this.getBaseBoxWidthPx();
		const runtimes: AnswerBoxView[] = [];

		for (let index = 0; index < count; index += 1) {
			const position = getAnswerBoxPosition(index, count);
			const fraction = getLaneFraction(index, this.laneLinesCount);
			const slotX = rowLeftX + rowWidthPx * fraction;
			runtimes.push(
				new AnswerBoxView({
					scene: this.scene,
					parent: this.rowContainer,
					x: slotX,
					position,
					index,
					labelText: this.labels[index] ?? '',
					animationAsset: this.getAnswerAsset(position),
					themeConfig: this.themeConfig,
					boxWidth,
					viewportWidth: this.viewportWidth,
					renderScaleX: this.renderScaleX,
					renderScaleY: this.renderScaleY,
					lottieSourceRoot: this.lottieSourceRoot,
				}),
			);
		}

		return runtimes;
	}

	// Keeps the row transform in sync with the current manual timeline progress.
	private syncTimelineState() {
		const stageProgress = Phaser.Math.Clamp(this.elapsedSeconds / INITIAL_LIFT_DURATION_SECONDS, 0, 1);
		const travelProgress = Phaser.Math.Clamp(
			(this.elapsedSeconds - INITIAL_LIFT_DURATION_SECONDS) / TRAVEL_DURATION_SECONDS,
			0,
			1,
		);
		const y =
			this.elapsedSeconds <= INITIAL_LIFT_DURATION_SECONDS
				? Phaser.Math.Linear(0, this.initialLiftY, stageProgress)
				: Phaser.Math.Linear(this.initialLiftY, this.bottomTargetY, travelProgress);
		const scale =
			this.elapsedSeconds <= INITIAL_LIFT_DURATION_SECONDS
				? Phaser.Math.Linear(ANSWER_ROW_INITIAL_SCALE, ANSWER_ROW_MIDDLE_SCALE, stageProgress)
				: Phaser.Math.Linear(ANSWER_ROW_MIDDLE_SCALE, ANSWER_ROW_FINAL_SCALE, travelProgress);

		this.rowContainer.setPosition(this.laneMetrics.centerX, this.laneMetrics.topOffset + y);
		this.rowContainer.setScale(scale);
		this.syncLayering();
	}

	// Applies the established pause/resume behavior to the animated answer box skins.
	private syncBoxAnimationPlayback() {
		this.runtimes.forEach((runtime) => runtime.setAnimationPaused(this.waitingAtBottom));
	}

	// Keeps the answer row below the lane during the lift phase and above it once downward travel begins.
	private syncLayering(force = false) {
		const shouldBeAboveLane = this.elapsedSeconds > INITIAL_LIFT_DURATION_SECONDS;

		if (!force && shouldBeAboveLane === this.rowAboveLane) {
			return;
		}

		if (shouldBeAboveLane) {
			this.container.bringToTop(this.rowContainer);
		} else {
			this.container.moveBelow(this.rowContainer, this.laneDisplayObject);
		}

		this.rowAboveLane = shouldBeAboveLane;
	}

	// Computes the bottom answer-row clearance using the same player-height logic as the established game.
	private getBottomTargetY(): number {
		const playerWidthPx =
			getResponsiveWidthPx(this.viewportWidth, this.themeConfig.widthPx) *
			this.playerThemeConfig.answerBoxWidthScale *
			this.renderScaleX;
		const playerHeightPx =
			(playerWidthPx * this.playerThemeConfig.animations.default.canvasHeight) /
			this.playerThemeConfig.animations.default.canvasWidth;
		const clearanceFactor = this.viewportWidth < SCREEN_SIZES.DESKTOP ? 0.8 : 1;
		const clearance = playerHeightPx * clearanceFactor;
		const panelHeightPx = ANSWER_PANEL_HEIGHT_PX * this.renderScaleY;

		return Math.max(0, this.laneMetrics.height - panelHeightPx - clearance);
	}

	// Keeps shorter static answer boxes connected to the lane after the initial upward reveal.
	private getInitialLiftY(): number {
		const initialLiftY = computeInitialLiftYFromWidth(this.viewportWidth) * this.renderScaleY;
		const leftAsset = this.themeConfig.animations.left;

		if (isLottieAnswerAsset(leftAsset)) {
			return initialLiftY;
		}

		const boxWidth = this.getBaseBoxWidthPx();
		const boxHeight = (boxWidth * leftAsset.assetSize.height) / leftAsset.assetSize.width;
		const maxStaticLift = boxHeight * (1 - STATIC_BOX_INITIAL_OVERLAP_RATIO);

		return Math.max(initialLiftY, -maxStaticLift);
	}

	// Applies the established success / failure colors, icons, and seconds delta to the resolved answer row.
	private decorateResolvedAnswer(resolution: LaneChooserAnswerResolution) {
		this.runtimes.forEach((runtime) => {
			if (!runtime.container.active) {
				return;
			}

			runtime.applyResolution(resolution);
		});
	}

	// Starts the accelerated post-jump exit move that sends the resolved row below the screen.
	private startExitTransition() {
		this.exitStarted = true;
		this.exitAbovePlayer = false;
		this.syncLayering(true);
		this.exitTween?.stop();
		this.scene.tweens.killTweensOf(this.rowContainer);

		const exitTargetY =
			this.laneMetrics.topOffset + this.laneMetrics.height + ANSWER_PANEL_HEIGHT_PX * this.renderScaleY;

		this.exitTween = this.scene.tweens.add({
			targets: this.rowContainer,
			y: exitTargetY,
			scaleX: EXIT_SCALE,
			scaleY: EXIT_SCALE,
			duration: EXIT_DURATION_MS,
			ease: 'Linear',
			onComplete: () => {
				this.exitTween = null;
				this.onRoundExitComplete();
			},
		});
	}

	// Resolves the themed visual asset for the requested slot position.
	private getAnswerAsset(position: AnswerBoxPosition): LaneChooserAnswerAsset {
		if (position === 'left') {
			return this.themeConfig.animations.left;
		}

		if (position === 'right') {
			return this.themeConfig.animations.right ?? this.themeConfig.animations.left;
		}

		return this.themeConfig.animations.middle;
	}

	// Chooses the same responsive base width buckets that the established SCSS used for answer boxes.
	private getBaseBoxWidthPx(): number {
		return getResponsiveWidthPx(this.viewportWidth, this.themeConfig.widthPx) * this.renderScaleX;
	}
}
